import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MetalWordmark } from "./MetalWordmark";
import { TAGLINE, TAGLINE_LINE_1, TAGLINE_SCRIPTURE } from "../brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;
const LF_LOGO =
  "https://customer-assets.emergentagent.com/job_exchange-landing/artifacts/21rxiv9x_life%20foundation%20%282400%20x%202400%20px%29.png";
const TEA_LOGO =
  "https://customer-assets.emergentagent.com/job_exchange-landing/artifacts/jqlejxxk_TEA%20Design%20LOGO%20001.png";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email });
      toast.success(data.already_subscribed ? "You're already subscribed." : "Subscribed. Watch for the next bulletin.");
      setEmail("");
    } catch { toast.error("Subscription failed. Try again."); }
    finally { setLoading(false); }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#0A0612] text-white border-t-2 border-[#D4AF37]">
      <div className="border-b border-[#D4AF37]/15">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-14 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mb-2">The Alliance Bulletin</div>
            <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-white">
              Quarterly outcomes. No spam. <span className="text-[#D4AF37]">Ever.</span>
            </h3>
          </div>
          <form onSubmit={subscribe} data-testid="newsletter-form" className="lg:col-span-7 flex flex-col sm:flex-row gap-3">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourorganization.org" data-testid="newsletter-email"
              className="flex-1 rounded-none bg-[#1A1A1A] border-[#D4AF37]/25 text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:border-[#D4AF37] h-12" />
            <Button type="submit" disabled={loading} data-testid="newsletter-submit"
              className="rounded-none bg-[#D4AF37] hover:bg-[#F4D88A] text-[#0A0612] font-mono text-xs tracking-[0.2em] uppercase h-12 px-6 disabled:opacity-50">
              {loading ? "Subscribing…" : "Subscribe"}
            </Button>
          </form>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-14 grid lg:grid-cols-12 gap-10">
        {/* Brand stack — restructured to a clean flex column so the medallion,
            wordmark, tagline, italic motto, and descriptor never compete on
            the same line. Local /tea-medallion.png (no remote 2× upscale that
            was causing overlapping text). */}
        <div className="lg:col-span-5" data-testid="footer-brand">
          <div className="flex items-center gap-4 mb-5">
            <img
              src="/tea-medallion.png"
              alt="The Exchange Alliance"
              className="h-14 w-14 shrink-0 object-contain"
              style={{ borderRadius: 0, background: "transparent" }}
            />
            <div className="flex flex-col" style={{ gap: 4, lineHeight: 1.4 }}>
              <div className="font-display font-bold text-base tracking-tight"><MetalWordmark small testId="footer-wordmark" /></div>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/70" data-testid="footer-tagline-short">{TAGLINE_LINE_1}</div>
            </div>
          </div>
          <p className="font-display italic text-xl tracking-tight text-[#D4AF37] leading-snug mb-2" data-testid="footer-tagline">
            "{TAGLINE}"
          </p>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]/60 mb-4" data-testid="footer-scripture">
            {TAGLINE_SCRIPTURE}
          </div>
          <p className="text-sm text-white/65 max-w-md leading-relaxed">
            A Christ-centered collaborative serving the Last, the Least and the Lost —
            replacing fragmented shelter systems with a high-accountability vocational
            residence model that carries people from the streets to stable careers.
          </p>
        </div>

        <FooterCol title="The Alliance" links={[
          ["The Journey", "#journey"],
          ["The Three Pillars", "#alliance"],
          ["Strategic Partners", "#partners"],
          ["Verified Partnerships", "/alliances/verified"],
        ]}/>
        <FooterCol title="Engage" links={[
          ["Get Assistance", "#assist"],
          ["Donate", "#donate"],
          ["Become an Employer", "/employers"],
          ["Watch the commercial", "/#commercial"],
        ]}/>

        {/* 🩹 PATCH-350 · Legal + compliance column so every page is
            one click from the footer and every reviewer can trace
            our claims to source documents. */}
        <FooterCol title="Legal" links={[
          ["Privacy Policy",   "/legal/privacy"],
          ["Terms of Service", "/legal/terms"],
          ["Security & Compliance", "/legal/security"],
          ["HIPAA Notice",     "/legal/hipaa"],
          ["Request a BAA",    "/legal/baa"],
        ]}/>

        <div className="lg:col-span-2">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mb-4">Pillars</div>
          <ul className="space-y-2 text-sm text-white/85">
            <li><span className="inline-block h-1.5 w-1.5 bg-[#1B4332] mr-2"></span>Triage &amp; Stabilization</li>
            <li><span className="inline-block h-1.5 w-1.5 bg-[#D4AF37] mr-2"></span>Residential Sanctuary</li>
            <li><span className="inline-block h-1.5 w-1.5 bg-[#0F766E] mr-2"></span>Career &amp; Independence</li>
          </ul>
          <a href="/login" data-testid="footer-partner-gateway"
            className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] hover:text-[#F4D88A] border-b border-[#D4AF37]/40 pb-1">
            Partner Gateway Access →
          </a>
        </div>
      </div>

      {/* 🩹 PATCH-350 · Legal-operator + compliance-posture bar. Retires
          the reviewer flags "Legal operator not obvious" +
          "Independent organizational footprint weak" by naming the
          operating entity, the governing body, and stating
          the honest compliance posture with links to source proof. */}
      <div className="border-t border-[#D4AF37]/15 bg-black/20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-6 grid lg:grid-cols-12 gap-6 text-[11px] text-white/60 leading-relaxed">
          <div className="lg:col-span-6" data-testid="footer-legal-operator">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mb-2">Operator of Record</div>
            <p className="text-white/80">
              <strong className="text-white">The Learning Institute for Empowerment, LLC</strong> (DBA <em>LIFE LLC</em>) —
              operating brand of a 501(c)(3) non-profit collaborative chartered by The Life Foundation.
            </p>
            <p className="mt-2">
              Registered address: 7433 Springside Drive, Myrtle Beach, SC 29588 · EIN 43-2209994.
              Contributions are tax-deductible to the fullest extent allowed by law.
            </p>
          </div>
          <div className="lg:col-span-6" data-testid="footer-compliance-posture">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mb-2">Compliance Posture</div>
            <p>
              Architected around SOC 2 Trust Services Criteria (Security, Confidentiality, Availability).
              AES-256 at rest · TLS 1.3 in transit · MFA required · immutable role-based audit logs.
              Independent SOC 2 Type I attestation <strong className="text-white/85">planned</strong>.
              HIPAA/HITECH-aligned architecture; formal risk assessment complete.{" "}
              <Link to="/legal/security" className="text-[#D4AF37]/85 hover:text-[#D4AF37] underline underline-offset-2">
                See the full disclosure →
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-[#D4AF37]/15">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]/60">
            © {new Date().getFullYear()} The Learning Institute for Empowerment, LLC · All rights reserved
          </div>
          <div className="text-[11px] text-white/50 max-w-3xl lg:text-right space-x-3">
            <Link to="/legal/privacy"  className="hover:text-[#D4AF37] transition-colors">Privacy</Link>
            <span className="text-white/20">·</span>
            <Link to="/legal/terms"    className="hover:text-[#D4AF37] transition-colors">Terms</Link>
            <span className="text-white/20">·</span>
            <Link to="/legal/security" className="hover:text-[#D4AF37] transition-colors">Security</Link>
            <span className="text-white/20">·</span>
            <Link to="/legal/hipaa"    className="hover:text-[#D4AF37] transition-colors">HIPAA</Link>
            <span className="text-white/20">·</span>
            <Link to="/legal/baa"      className="hover:text-[#D4AF37] transition-colors">BAA</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div className="lg:col-span-2">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mb-4">{title}</div>
      <ul className="space-y-2">
        {links.map(([label, href]) => {
          // 🩹 PATCH-350 · use <Link> for real routes so we stay in the
          // SPA + preserve session state; keep <a> for legacy anchor
          // targets (#journey, #alliance, etc.) so the smooth-scroll
          // marketing shell keeps working.
          const isAnchor = href.startsWith("#");
          return (
            <li key={label}>
              {isAnchor ? (
                <a href={window.location.pathname === "/" ? href : `/${href}`} className="text-sm text-white/85 hover:text-[#D4AF37] transition-colors">{label}</a>
              ) : (
                <Link to={href} className="text-sm text-white/85 hover:text-[#D4AF37] transition-colors">{label}</Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
